import { supabase } from '@/lib/supabase'
import type { Scope } from '@/lib/db-types'
import type { PaymentMethodBrand, PaymentMethodType } from '@/lib/schemas'

// NOTE: payment_methods is not yet in src/lib/database.types.ts because
// `npm run gen:types` has not run after migrations 033-035 were applied.
// Until then, we use an untyped supabase client locally and cast results.
// After regenerating types, switch to Database['public']['Tables']['payment_methods'].
const db = supabase as unknown as {
  from: (table: string) => any
  storage: typeof supabase.storage
}

const IMAGE_BUCKET = 'payment-method-images'
const SIGNED_URL_TTL_SECONDS = 60 * 30 // 30 minutes

export type PaymentMethod = {
  id: string
  type: PaymentMethodType
  scope: Scope
  name: string
  color: string | null
  image_path: string | null
  // credit_card fields (nullable for bank_account)
  brand: PaymentMethodBrand | null
  last4: string | null
  closing_day: number | null
  closing_day_end_of_month: boolean
  payment_day: number | null
  payment_day_end_of_month: boolean
  payment_month_offset: 1 | 2
  // bank_account fields
  bank_name: string | null
  branch_name: string | null
  // credit_card → bank_account のリンク (型は同じテーブル内の payment_method.id)
  linked_bank_method_id: string | null
  // meta
  sort_order: number
  is_active: boolean
  created_by: string
  created_at: string
  updated_at: string
}

export type PaymentMethodInsert = Omit<PaymentMethod, 'id' | 'created_at' | 'updated_at'>
export type PaymentMethodUpdate = Partial<Omit<PaymentMethod, 'id' | 'created_by' | 'created_at' | 'updated_at'>>

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  const { data, error } = await db
    .from('payment_methods')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })

  if (error) throw new Error(error.message)
  return (data ?? []) as PaymentMethod[]
}

export async function reorderPaymentMethods(
  orders: { id: string; sort_order: number }[],
): Promise<void> {
  if (orders.length === 0) return
  const results = await Promise.all(
    orders.map((o) =>
      db.from('payment_methods').update({ sort_order: o.sort_order }).eq('id', o.id),
    ),
  )
  for (const { error } of results as { error: { message: string } | null }[]) {
    if (error) throw new Error(error.message)
  }
}

export async function fetchPaymentMethodById(id: string): Promise<PaymentMethod | null> {
  const { data, error } = await db
    .from('payment_methods')
    .select('*')
    .eq('id', id)
    .maybeSingle()

  if (error) throw new Error(error.message)
  return (data as PaymentMethod | null) ?? null
}

export async function createPaymentMethod(input: PaymentMethodInsert): Promise<PaymentMethod> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Unauthorized')

  const { data, error } = await db
    .from('payment_methods')
    .insert({ ...input, created_by: user.id })
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as PaymentMethod
}

export async function updatePaymentMethod(id: string, patch: PaymentMethodUpdate): Promise<PaymentMethod> {
  const { data, error } = await db
    .from('payment_methods')
    .update(patch)
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as PaymentMethod
}

export async function deletePaymentMethod(id: string): Promise<void> {
  // Delete the image first to avoid orphaned Storage objects. If Storage delete
  // fails we abort so the DB row is preserved and the user can retry.
  const existing = await fetchPaymentMethodById(id)
  if (existing?.image_path) {
    const { error: storageError } = await supabase.storage
      .from(IMAGE_BUCKET)
      .remove([existing.image_path])
    if (storageError) throw new Error(`画像の削除に失敗しました: ${storageError.message}`)
  }

  const { error } = await db
    .from('payment_methods')
    .delete()
    .eq('id', id)

  if (error) throw new Error(error.message)
}

export async function uploadPaymentMethodImage(
  paymentMethodId: string,
  file: File,
): Promise<string> {
  const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase()
  const path = `${paymentMethodId}/image.${ext}`

  // If a previous image exists with a different extension, remove it so we
  // don't leak orphaned files when the user re-uploads with another format.
  const existing = await fetchPaymentMethodById(paymentMethodId)
  if (existing?.image_path && existing.image_path !== path) {
    await supabase.storage.from(IMAGE_BUCKET).remove([existing.image_path])
  }

  const { error: uploadError } = await supabase.storage
    .from(IMAGE_BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type })
  if (uploadError) throw new Error(uploadError.message)

  await updatePaymentMethod(paymentMethodId, { image_path: path })
  return path
}

export async function deletePaymentMethodImage(paymentMethodId: string): Promise<void> {
  const existing = await fetchPaymentMethodById(paymentMethodId)
  if (!existing?.image_path) return

  const { error } = await supabase.storage
    .from(IMAGE_BUCKET)
    .remove([existing.image_path])
  if (error) throw new Error(error.message)

  await updatePaymentMethod(paymentMethodId, { image_path: null })
}

export async function fetchPaymentMethodImageUrls(
  paths: string[],
): Promise<Record<string, string>> {
  if (paths.length === 0) return {}
  const { data, error } = await supabase.storage
    .from(IMAGE_BUCKET)
    .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS)

  if (error) throw new Error(error.message)
  const map: Record<string, string> = {}
  for (const entry of data ?? []) {
    if (entry.path && entry.signedUrl) {
      map[entry.path] = entry.signedUrl
    }
  }
  return map
}

export const PAYMENT_METHOD_IMAGE_URL_TTL_MS = SIGNED_URL_TTL_SECONDS * 1000
